import { OrderLineItem } from '../contexts/MenuContext';
import { apiFetch } from '../utils/apiClient';

export type GuestOrderStatus = 'OPEN' | 'NEW' | 'PENDING' | 'CHECKOUT_REQUESTED' | 'PAID' | 'CLOSED';

export interface GuestOrderResponse {
    id: number;
    tableNumber: string;
    orderCode: string;
    customerName?: string | null;
    status?: string | null;
    orderItems?: string | null;
    totalAmount?: number | null;
    isPaid?: boolean | null;
}

export interface GuestOrderSnapshot {
    orderCode: string;
    tableNumber: string;
    customerName?: string;
    status?: GuestOrderStatus;
    orderItems: OrderLineItem[];
    totalAmount: number;
    isPaid: boolean;
    lastUpdatedAt?: string;
}

export interface GuestOrderRequest {
    tableNumber: string;
    customerName?: string;
    status?: GuestOrderStatus;
    items: OrderLineItem[];
    totalAmount: number;
    isPaid?: boolean;
}

const sanitizeComment = (value: unknown): string | undefined => {
    if (typeof value !== 'string') {
        return undefined;
    }
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
};

const sanitizeOrderItem = (item: OrderLineItem): OrderLineItem => {
    const comment = sanitizeComment(item.comment);
    return {
        ...item,
        comment,
    };
};

const sanitizeOrderItems = (items: OrderLineItem[]): OrderLineItem[] => items.map(sanitizeOrderItem);

function serializeOrderItems(items: OrderLineItem[]): string {
    return JSON.stringify({ items: sanitizeOrderItems(items) });
}

function deserializeOrderItems(orderItems?: string | null): OrderLineItem[] {
    if (!orderItems) return [];
    try {
        const parsed = JSON.parse(orderItems);
        if (Array.isArray(parsed)) {
            return sanitizeOrderItems(parsed as OrderLineItem[]);
        }
        if (Array.isArray(parsed?.items)) {
            return sanitizeOrderItems(parsed.items as OrderLineItem[]);
        }
    } catch (error) {
        console.warn('Impossibile convertire gli articoli ordine:', error);
    }
    return [];
}

async function extractErrorMessage(res: Response): Promise<string> {
    try {
        const raw = await res.text();
        if (!raw) return `HTTP ${res.status}`;
        try {
            const parsed = JSON.parse(raw);
            return parsed.message || parsed.error || raw;
        } catch (error) {
            return raw;
        }
    } catch (error) {
        return `HTTP ${res.status}`;
    }
}

function mapResponse(data: GuestOrderResponse): GuestOrderSnapshot {
    return {
        orderCode: data.orderCode,
        tableNumber: data.tableNumber,
        customerName: data.customerName ?? undefined,
        status: (data.status as GuestOrderStatus | undefined) ?? undefined,
        orderItems: deserializeOrderItems(data.orderItems),
        totalAmount: typeof data.totalAmount === 'number' ? data.totalAmount : 0,
        isPaid: Boolean(data.isPaid),
        lastUpdatedAt: new Date().toISOString()
    };
}

export async function createGuestOrder(tenantId: string, request: GuestOrderRequest): Promise<GuestOrderSnapshot> {
    const payload = {
        tableNumber: request.tableNumber,
        customerName: request.customerName,
        status: request.status ?? 'OPEN',
        orderItems: serializeOrderItems(request.items),
        totalAmount: request.totalAmount,
        isPaid: request.isPaid ?? false
    };

    const res = await apiFetch('/api/public/orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        tenantId
    });

    if (!res.ok) {
        throw new Error(await extractErrorMessage(res));
    }

    const data = await res.json();
    return mapResponse(data as GuestOrderResponse);
}

export async function updateGuestOrder(tenantId: string, orderCode: string, request: GuestOrderRequest): Promise<GuestOrderSnapshot> {
    const payload = {
        tableNumber: request.tableNumber,
        customerName: request.customerName,
        status: request.status ?? 'OPEN',
        orderItems: serializeOrderItems(request.items),
        totalAmount: request.totalAmount,
        isPaid: request.isPaid ?? false
    };

    const res = await apiFetch(`/api/public/orders/${orderCode}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        tenantId
    });

    if (!res.ok) {
        throw new Error(await extractErrorMessage(res));
    }

    const data = await res.json();
    return mapResponse(data as GuestOrderResponse);
}

export async function fetchGuestOrder(tenantId: string, orderCode: string): Promise<GuestOrderSnapshot | null> {
    const res = await apiFetch(`/api/public/orders/${orderCode}`, {
        method: 'GET',
        tenantId
    });

    if (res.status === 404) return null;
    if (!res.ok) {
        throw new Error(await extractErrorMessage(res));
    }

    const data = await res.json();
    return mapResponse(data as GuestOrderResponse);
}

export async function requestCheckout(tenantId: string, orderCode: string, paymentMethod: string): Promise<GuestOrderSnapshot> {
    const payload = {
        paymentMethod: paymentMethod.toUpperCase(),
        customerNote: ''
    };

    const res = await apiFetch(`/api/public/orders/${orderCode}/request-checkout`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        tenantId
    });

    if (!res.ok) {
        throw new Error(await extractErrorMessage(res));
    }

    const data = await res.json();
    return mapResponse(data as GuestOrderResponse);
}

export function mergeOrderItems(base: OrderLineItem[], additions: OrderLineItem[]): OrderLineItem[] {
    const normalizedBase = sanitizeOrderItems(base);
    const normalizedAdditions = sanitizeOrderItems(additions);

    const map = new Map<string, OrderLineItem>();
    normalizedBase.forEach(item => {
        map.set(item.productId, { ...item });
    });

    normalizedAdditions.forEach(item => {
        const current = map.get(item.productId);
        if (current) {
            map.set(item.productId, {
                ...current,
                quantity: current.quantity + item.quantity,
                price: item.price,
                comment: item.comment ?? current.comment,
            });
        } else {
            map.set(item.productId, { ...item });
        }
    });

    return sanitizeOrderItems(Array.from(map.values()));
}
